import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["all.color", "color"],
  _excluded2 = ["color"];
import { BORDER, COLOR, GROUP } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { modifyInheritedValue } from 'ContentComponents/components/cmv2/utils/inheritance';
import CustomWidgetModifierOperators from 'ContentUtils/constants/CustomWidgetModifierOperators';
import { SHADE_POSITION_NAMES } from 'ContentUtils/constants/ColorShadePositions';
import { getAnchorPosition, getShadeScale, getTokens, resolveOffsetIndex } from 'ContentUtils/helpers/ColorsHelpers';
export const scanFieldTree = fields => {
  const fieldsToVisit = [...fields];
  let hasCollection = false;
  let hasGlobalStylesBindings = false;
  while (fieldsToVisit.length > 0 && !(hasCollection && hasGlobalStylesBindings)) {
    var _field$binding;
    const field = fieldsToVisit.pop();
    if (field.collection != null) hasCollection = true;
    if (((_field$binding = field.binding) === null || _field$binding === void 0 ? void 0 : _field$binding.context) === 'global_styles') hasGlobalStylesBindings = true;
    if (field.type === GROUP && field.children) {
      fieldsToVisit.push(...field.children);
    }
  }
  return {
    hasCollection,
    hasGlobalStylesBindings
  };
};

/**
 * Workaround: the BE preview-CSS resolver only honors `all.color` for
 * border-field bindings, but the FE (BoundColorPickerPopover + GSS variant
 * defaults) produces `color`-keyed paths to match ColorField's shape.
 * Translate at this boundary so persisted state stays in the BE-aligned
 * `all.color` shape, while the inner ColorPicker keeps reading `color`.
 *
 * Tracking: https://github.com/HubSpotEngineering/ReusableAssetsTeam/issues/1238
 */
const borderBindingForDisplay = binding => {
  const paths = binding === null || binding === void 0 ? void 0 : binding.property_value_paths;
  if (!paths || !paths['all.color']) return binding;
  // If both `all.color` and `color` are present, prefer `all.color`
  const {
      'all.color': allColor
    } = paths,
    rest = _objectWithoutPropertiesLoose(paths, _excluded);
  return Object.assign({}, binding, {
    property_value_paths: Object.assign({}, rest, {
      color: allColor
    })
  });
};
const borderBindingForWire = ref => {
  var _rest$allColor;
  const paths = ref === null || ref === void 0 ? void 0 : ref.property_value_paths;
  if (!paths || !paths.color) return ref;
  const {
      color
    } = paths,
    rest = _objectWithoutPropertiesLoose(paths, _excluded2);
  return Object.assign({}, ref, {
    property_value_paths: Object.assign({}, rest, {
      'all.color': (_rest$allColor = rest['all.color']) !== null && _rest$allColor !== void 0 ? _rest$allColor : color
    })
  });
};
export const resolveFieldBindingProps = (field, fieldKey, context) => {
  var _ref, _resolvedBindings;
  const {
    binding
  } = field;
  if (!binding || !context) return {};
  const {
    bindings,
    collectionMap,
    resolvedBindings,
    isGlobalStylesLoading,
    onBindingChange
  } = context;
  if (binding.context === 'global_styles' && isGlobalStylesLoading) {
    return {
      isBindingLoading: true
    };
  }
  const fieldPath = fieldKey.join('.');
  const collectionData = collectionMap[binding.collection];
  const isBorderField = field.type === BORDER;
  // resolvedBindings normalizes shade bindings to direct source references for the popover.
  // It handles both user picks and variant defaults, so check it first.
  const normalizedBinding = (_ref = (_resolvedBindings = resolvedBindings[`${fieldPath}.color`]) !== null && _resolvedBindings !== void 0 ? _resolvedBindings : bindings[fieldPath]) !== null && _ref !== void 0 ? _ref : field.inherited_value;
  return {
    collectionData,
    currentBinding: isBorderField ? borderBindingForDisplay(normalizedBinding) : normalizedBinding,
    onBindingChange: ref => onBindingChange(fieldPath, isBorderField ? borderBindingForWire(ref) : ref)
  };
};

/**
 * Maps each bindable field type to the properties on its value object that hold collectible values.
 * Each entry is a string array naming the sub-properties to extract (e.g. ['color'] → fieldValue.color).
 *
 * An entry is required for any field type that should participate in collections.
 * Field types without an entry are skipped entirely during collection map building.
 */
const COLLECTION_VALUE_PROP_BY_FIELD_TYPE = {
  [COLOR]: ['color'],
  [BORDER]: ['all.color']
};

// TODO: The existing inherited_value resolver in inheritance.ts resolves property_value_paths by
// walking module data via getDeepValue. This resolver uses the CollectionMap instead (O(1) lookup).
// Consider whether the two can share logic once the binding feature matures.

/**
 * Resolves all binding paths in a binding reference against a specific collection's entries.
 *
 * The caller is responsible for selecting the correct collection from the CollectionMap
 * using the collection ID from field.binding.collection before calling this function.
 *
 * @param bindingRef - An InheritedValue whose property_value_paths point into the collection
 * @param collectionEntries - The entries for the specific collection: collectionMap[field.binding.collection].entries
 * @returns A map of prop key → resolved value for each path that resolves; empty if nothing resolves
 *
 * @example
 * const resolved = resolveBindingReference(
 *   { property_value_paths: { color: "palette.accent.color" } },
 *   collectionMap[field.binding.collection].entries
 * );
 * // { color: "#FF5733" }
 */
// Path prefix the BE uses for variant field references (e.g. variant.groupSection.backgroundColor.color)
const VARIANT_PREFIX = 'variant.';
// GS binding paths arrive `theme.`-prefixed on the wire, while `resolvedBindings`
// is keyed form-relative. See the note on `buildCollectionMap` (TEMP, RAT #1238).
const THEME_PATH_PREFIX = 'theme.';
export const resolveBindingReference = (bindingRef, collectionEntries) => {
  const {
    property_value_paths: propertyValuePaths,
    modifier
  } = bindingRef;
  const {
    modifier_type,
    modifier_value
  } = modifier !== null && modifier !== void 0 ? modifier : {};
  // TODO: also support default_value_path as a single direct path into the collection entries,
  // similar to how inheritance.ts uses it for full-value references (useful for scalar collection fields)

  if (!propertyValuePaths) return {};
  const result = {};
  for (const [propKey, sourcePath] of Object.entries(propertyValuePaths)) {
    const resolved = collectionEntries[sourcePath];
    if (resolved == null) continue;
    result[propKey] = modifier_type && modifier_value != null ? modifyInheritedValue(resolved, modifier_type, modifier_value) : resolved;
  }
  return result;
};
function _resolveFieldValue(field, overrides) {
  const override = overrides[field.name];
  if (override === undefined) {
    return {
      value: field.default,
      hasOverride: false
    };
  }
  if (field.default && !Array.isArray(field.default) && typeof field.default === 'object' && typeof override === 'object') {
    return {
      value: Object.assign({}, field.default, override),
      hasOverride: true
    };
  }
  return {
    value: override,
    hasOverride: true
  };
}
function _ensureCollection(map, collectionId) {
  if (!map[collectionId]) {
    map[collectionId] = {
      resolvedValues: {},
      cssVariables: {},
      renderShades: false
    };
  }
  return map[collectionId];
}
function _mergeCollectionData(existing, child) {
  var _existing$resolvedVal, _existing$cssVariable, _existing$renderShade;
  return {
    resolvedValues: Object.assign({}, (_existing$resolvedVal = existing === null || existing === void 0 ? void 0 : existing.resolvedValues) !== null && _existing$resolvedVal !== void 0 ? _existing$resolvedVal : {}, child.resolvedValues),
    cssVariables: Object.assign({}, (_existing$cssVariable = existing === null || existing === void 0 ? void 0 : existing.cssVariables) !== null && _existing$cssVariable !== void 0 ? _existing$cssVariable : {}, child.cssVariables),
    renderShades: ((_existing$renderShade = existing === null || existing === void 0 ? void 0 : existing.renderShades) !== null && _existing$renderShade !== void 0 ? _existing$renderShade : false) || child.renderShades
  };
}
function _writeEntries(collectionData, fieldPath, resolved, cssVariable) {
  let changed = false;
  for (const [propKey, propValue] of Object.entries(resolved)) {
    const entryPath = `${fieldPath}.${propKey}`;
    if (collectionData.resolvedValues[entryPath] !== propValue) {
      collectionData.resolvedValues[entryPath] = propValue;
      changed = true;
    }
    if (cssVariable) {
      collectionData.cssVariables[entryPath] = cssVariable;
    }
  }
  return changed;
}
function _getCollectionId(field) {
  if (field.collection) {
    return {
      collectionId: field.collection,
      isConsumer: false
    };
  }
  return {
    collectionId: field.binding.collection,
    isConsumer: true
  };
}
const _getNestedValue = (value, propKey) => propKey.split('.').reduce((acc, key) => acc == null ? acc : acc[key], value);
function _buildCollectionMapInner(fields, overrides, pathPrefix, consumers) {
  return fields.reduce((acc, field) => {
    const propKeys = COLLECTION_VALUE_PROP_BY_FIELD_TYPE[field.type];
    if (field.type !== GROUP && (propKeys === undefined || !field.collection && !field.binding)) {
      return acc;
    }
    const {
      value,
      hasOverride
    } = _resolveFieldValue(field, overrides);
    if (field.type === GROUP) {
      if (field.children) {
        const childMap = _buildCollectionMapInner(field.children, value || {}, [...pathPrefix, field.name], consumers);
        for (const collectionId of Object.keys(childMap)) {
          acc[collectionId] = _mergeCollectionData(acc[collectionId], childMap[collectionId]);
        }
      }
      return acc;
    }
    const {
      collectionId,
      isConsumer
    } = _getCollectionId(field);
    const fieldPath = [...pathPrefix, field.name].join('.');
    for (const propKey of propKeys) {
      // propKey may be a dotted path (e.g. a border's `all.color`); resolve it
      // against the nested field value rather than as a literal top-level key.
      const propValue = _getNestedValue(value, propKey);
      if (propValue != null && (field.type === COLOR || field.type === BORDER)) {
        const data = _ensureCollection(acc, collectionId);
        if (field.render_shades) data.renderShades = true;
        // Border colors live at `all.color`; store them under the `.color` path
        // the rest of the shade pipeline and dependent bindings reference.
        const entryKey = field.type === BORDER ? 'color' : propKey;
        data.resolvedValues[`${fieldPath}.${entryKey}`] = propValue;
        if (field.css_variable) {
          data.cssVariables[`${fieldPath}.${entryKey}`] = field.css_variable;
        }
      }
    }
    if (isConsumer) {
      consumers.push({
        field,
        fieldPath,
        collectionId,
        hasOverride
      });
    }
    return acc;
  }, {});
}
const SHADE_INDEX_TO_NAME = ['LIGHTER', 'LIGHT', 'MEDIUM', 'DARK', 'DARKER'];
function _resolveSourceEntry(colorPath, collectionData, resolvedBindings) {
  var _resolvedBindings$col;
  const {
    sourceEntries
  } = collectionData;
  if (!sourceEntries) return undefined;
  const direct = sourceEntries[colorPath];
  if (direct !== null && direct !== void 0 && direct.shadeScale) {
    return {
      scale: direct.shadeScale,
      sourcePath: colorPath
    };
  }
  const tracedPath = resolvedBindings === null || resolvedBindings === void 0 || (_resolvedBindings$col = resolvedBindings[colorPath]) === null || _resolvedBindings$col === void 0 || (_resolvedBindings$col = _resolvedBindings$col.property_value_paths) === null || _resolvedBindings$col === void 0 ? void 0 : _resolvedBindings$col.color;
  if (!tracedPath) return undefined;
  const traced = sourceEntries[tracedPath];
  return traced !== null && traced !== void 0 && traced.shadeScale ? {
    scale: traced.shadeScale,
    sourcePath: tracedPath
  } : undefined;
}
function _pickShade(scale, shadeName, offset = 0) {
  const tokens = getTokens(scale);
  const baseHex = tokens[shadeName];
  if (!baseHex) return undefined;
  const baseIndex = scale.indexOf(baseHex);
  if (baseIndex < 0) return undefined;
  const targetIndex = offset === 0 ? baseIndex : resolveOffsetIndex(baseIndex, offset, scale.length);
  const resolvedName = offset === 0 ? shadeName : SHADE_INDEX_TO_NAME.find(name => tokens[name] === scale[targetIndex]);
  if (!resolvedName) return undefined;
  return {
    hex: scale[targetIndex],
    resolvedName,
    index: targetIndex
  };
}
function _resolveFromShadeScale(inheritedValue, collectionData, resolvedBindings) {
  var _inheritedValue$prope;
  const colorPath = (_inheritedValue$prope = inheritedValue.property_value_paths) === null || _inheritedValue$prope === void 0 ? void 0 : _inheritedValue$prope.color;
  const modifier = inheritedValue.modifier;
  if (!colorPath || !modifier) return undefined;
  const {
    modifier_type,
    modifier_value
  } = modifier;
  const isShade = modifier_type === CustomWidgetModifierOperators.SHADE && typeof modifier_value === 'string';
  const isShadeOffset = modifier_type === CustomWidgetModifierOperators.SHADE_OFFSET && typeof modifier_value === 'number';
  if (!isShade && !isShadeOffset) return undefined;
  if (isShade) {
    const source = _resolveSourceEntry(colorPath, collectionData, resolvedBindings);
    if (!source) return undefined;
    const shade = _pickShade(source.scale, modifier_value);
    if (!shade) return undefined;
    return {
      resolved: {
        color: shade.hex
      },
      sourceBinding: {
        property_value_paths: {
          color: source.sourcePath
        },
        modifier: {
          modifier_type: CustomWidgetModifierOperators.SHADE,
          modifier_value: shade.resolvedName
        }
      }
    };
  }
  const source = _resolveSourceEntry(colorPath, collectionData, resolvedBindings);
  if (!source) return undefined;
  const targetBinding = resolvedBindings === null || resolvedBindings === void 0 ? void 0 : resolvedBindings[colorPath];
  const targetMod = targetBinding === null || targetBinding === void 0 ? void 0 : targetBinding.modifier;
  const targetShadeName = (targetMod === null || targetMod === void 0 ? void 0 : targetMod.modifier_type) === CustomWidgetModifierOperators.SHADE && typeof targetMod.modifier_value === 'string' ? targetMod.modifier_value : undefined;
  let shade;
  if (targetShadeName) {
    // Target has a SHADE modifier - offset from its named position
    shade = _pickShade(source.scale, targetShadeName, modifier_value);
  } else {
    // Target has no SHADE modifier (direct source ref) - find the anchor's shade name and offset from it
    const sourceHex = collectionData.sourceEntries[source.sourcePath].hex;
    const anchorIndex = getAnchorPosition(sourceHex);
    const anchorShadeName = SHADE_INDEX_TO_NAME.find(name => getTokens(source.scale)[name] === source.scale[anchorIndex]);
    if (!anchorShadeName) return undefined;
    shade = _pickShade(source.scale, anchorShadeName, modifier_value);
  }
  if (!shade) return undefined;
  return {
    resolved: {
      color: shade.hex
    },
    sourceBinding: {
      property_value_paths: {
        color: source.sourcePath
      },
      modifier: {
        modifier_type: CustomWidgetModifierOperators.SHADE,
        modifier_value: shade.resolvedName
      }
    }
  };
}
function _resolveConsumers(consumers, collectionMap) {
  const maxPasses = consumers.length;
  for (let pass = 0; pass < maxPasses; pass++) {
    let changed = false;
    for (const {
      field,
      fieldPath,
      collectionId,
      hasOverride
    } of consumers) {
      if (hasOverride) continue;

      // Border bindings are keyed on `all.color`; normalize to the `color` key
      // the shade resolver and picker use (no-op for color fields).
      const inheritedValue = borderBindingForDisplay(field.inherited_value);
      if (!(inheritedValue !== null && inheritedValue !== void 0 && inheritedValue.property_value_paths)) continue;
      const collectionData = collectionMap[collectionId];
      if (!collectionData) continue;
      const shadeResult = _resolveFromShadeScale(inheritedValue, collectionData);
      const resolved = shadeResult ? shadeResult.resolved : resolveBindingReference(inheritedValue, collectionData.resolvedValues);
      if (Object.keys(resolved).length === 0) continue;
      if (_writeEntries(collectionData, fieldPath, resolved, field.css_variable)) {
        changed = true;
      }
    }
    if (!changed) break;
  }
  for (const {
    field,
    fieldPath,
    collectionId
  } of consumers) {
    const data = collectionMap[collectionId];
    if (!data) continue;
    if (!data.consumers) data.consumers = [];
    data.consumers.push({
      fieldPath,
      inheritedValue: borderBindingForDisplay(field.inherited_value)
    });
  }
}

/**
 * Traverses a field tree and builds a map of collection IDs to property path → resolved value.
 * Used to generate the runtime lookup table for binding resolution.
 *
 * Source fields (with `collection`) store their values directly. Consumer fields (with `binding`)
 * also store their concrete values, then a second pass resolves `inherited_value` chains so that
 * one bound field can reference another bound field's resolved value.
 *
 * @param fields - The field spec to traverse
 * @param overrides - Current user-set values (mirrors module_body shape)
 * @param pathPrefix - Dot-path segments prepended to every entry path. Recursion uses this
 *   to accumulate parent group names; GS callers also pass `['theme']` at the top level
 *   to match the BE's `theme.`-prefixed binding-path wire format (TEMP, RAT #1238).
 * @param bindings - Runtime binding state (from variant configs / user picks). Used to resolve
 *   consumer values that have null module body entries because their color comes from bindings.
 * @param variantFieldKeyPrefix - The variant management field key from the variant system.
 *   Used to derive variant.* storage paths that match the BE's path format.
 * @returns A CollectionMap keyed by collection ID, then by dotted property path to resolved value
 */
export const buildCollectionMap = (fields, overrides = {}, pathPrefix = [], bindings = {}, variantFieldKeyPrefix = []) => {
  const consumers = [];
  const collectionMap = _buildCollectionMapInner(fields, overrides, pathPrefix, consumers);
  if (consumers.length > 0) {
    const consumerCollectionIds = new Set(consumers.map(c => c.collectionId));
    for (const collectionId of consumerCollectionIds) {
      const data = collectionMap[collectionId];
      if (!data) continue;
      const sourceEntries = {};
      for (const [path, value] of Object.entries(data.resolvedValues)) {
        const isConsumerEntry = consumers.some(c => c.collectionId === collectionId && path.startsWith(`${c.fieldPath}.`));
        if (!isConsumerEntry) {
          sourceEntries[path] = {
            hex: value,
            shadeScale: data.renderShades ? getShadeScale(value).scale : undefined
          };
        }
      }
      data.sourceEntries = sourceEntries;
    }

    // Resolve variant consumer values from runtime bindings into resolvedValues.
    // Only runs when variantFieldKeyPrefix is set (we're in a variant context).
    // Variant-bound fields have null module body values - the color comes from the binding.
    // Without this step, chained fields can't reference variant consumers in resolvedValues.
    if (variantFieldKeyPrefix.length > 0) {
      const prefix = pathPrefix.length > 0 ? `${pathPrefix.join('.')}.` : '';
      // Used to strip the absolute path back to the variant-relative path for variant.* storage
      const variantMgmtPrefix = `${variantFieldKeyPrefix.join('.')}.`;

      // Single-pass: a consumer referencing another consumer's variant.* path only
      // resolves if that consumer appears earlier in the field tree (declaration order).
      // Convert to fixed-point loop if multi-hop variant chains are needed.
      for (const consumer of consumers) {
        // Strip pathPrefix (e.g. 'theme.') to get the form-relative path that matches binding keys
        const formPath = prefix && consumer.fieldPath.startsWith(prefix) ? consumer.fieldPath.slice(prefix.length) : consumer.fieldPath;
        // Border bindings are keyed on `all.color`; normalize to `color` (no-op
        // for color fields) so the source lands in the collection map under the
        // `.color` path that dependent SHADE_OFFSET bindings look up.
        const binding = borderBindingForDisplay(bindings[formPath]);
        if (!(binding !== null && binding !== void 0 && binding.property_value_paths)) continue;
        const data = collectionMap[consumer.collectionId];
        if (!data) continue;

        // Resolve the binding against existing resolvedValues (source colors)
        const resolved = resolveBindingReference(binding, data.resolvedValues);
        if (Object.keys(resolved).length === 0) continue;

        // Store at variant.* path so other variant bindings can reference this field
        const variantRelPath = formPath.startsWith(variantMgmtPrefix) ? formPath.slice(variantMgmtPrefix.length) : formPath;
        _writeEntries(data, `${VARIANT_PREFIX}${variantRelPath}`, resolved, consumer.field.css_variable);
      }
    }
    _resolveConsumers(consumers, collectionMap);
  }
  return collectionMap;
};
export function resolveChainedBindings(collectionMap, bindings, pathPrefix = [], variantFieldKeyPrefix = []) {
  const result = {};
  const prefix = pathPrefix.length > 0 ? `${pathPrefix.join('.')}.` : '';
  const variantMgmtPrefix = variantFieldKeyPrefix.length > 0 ? `${variantFieldKeyPrefix.join('.')}.` : '';
  for (const collectionData of Object.values(collectionMap)) {
    if (!collectionData.consumers) continue;
    const consumerList = collectionData.consumers;
    const maxPasses = consumerList.length;
    for (let pass = 0; pass < maxPasses; pass++) {
      let changed = false;
      for (const consumer of consumerList) {
        var _collectionData$sourc;
        const entryPath = `${consumer.fieldPath}.color`;
        if (result[entryPath]) continue;
        const formFieldPath = prefix && consumer.fieldPath.startsWith(prefix) ? consumer.fieldPath.slice(prefix.length) : consumer.fieldPath;
        // Border bindings are keyed on `all.color`; normalize to `color` (no-op
        // for color fields) so the shade resolver and picker can read them.
        const userBinding = borderBindingForDisplay(bindings[formFieldPath]);
        // Use the user/variant binding if present, otherwise fall back to inherited_value
        const bindingToResolve = userBinding !== null && userBinding !== void 0 ? userBinding : borderBindingForDisplay(consumer.inheritedValue);
        if (!(bindingToResolve !== null && bindingToResolve !== void 0 && bindingToResolve.property_value_paths)) continue;

        // Try shade normalization (works for both user picks and variant defaults)
        const shadeResult = _resolveFromShadeScale(bindingToResolve, collectionData, result);
        // In variant context, store at both variant.* path (for other variant bindings
        // to reference) and form-relative path (for resolveFieldBindingProps lookup)
        const variantEntryPath = variantMgmtPrefix && formFieldPath.startsWith(variantMgmtPrefix) ? `${VARIANT_PREFIX}${formFieldPath.slice(variantMgmtPrefix.length)}.color` : null;
        const storeEntryPath = entryPath;
        if (shadeResult) {
          result[storeEntryPath] = shadeResult.sourceBinding;
          if (variantEntryPath) result[variantEntryPath] = shadeResult.sourceBinding;
          changed = true;
          continue;
        }

        // For non-shade user bindings (direct picks), store as-is
        if (userBinding) {
          result[storeEntryPath] = userBinding;
          if (variantEntryPath) result[variantEntryPath] = userBinding;
          changed = true;
          continue;
        }
        const targetPath = bindingToResolve.property_value_paths.color;
        if (!targetPath) continue;
        const targetBinding = result[targetPath];
        if (targetBinding) {
          result[storeEntryPath] = targetBinding;
          if (variantEntryPath) result[variantEntryPath] = targetBinding;
          changed = true;
        } else if ((_collectionData$sourc = collectionData.sourceEntries) !== null && _collectionData$sourc !== void 0 && _collectionData$sourc[targetPath] && !bindingToResolve.modifier) {
          const sourceRef = {
            property_value_paths: {
              color: targetPath
            }
          };
          result[storeEntryPath] = sourceRef;
          if (variantEntryPath) result[variantEntryPath] = sourceRef;
          changed = true;
        }
      }
      if (!changed) break;
    }
  }
  if (prefix) {
    const rekeyed = {};
    for (const [key, value] of Object.entries(result)) {
      rekeyed[key.startsWith(prefix) ? key.slice(prefix.length) : key] = value;
    }
    return rekeyed;
  }
  return result;
}
const normalizeCssVarName = name => name.startsWith('--') ? name : `--${name}`;
export function inheritedValueToCssVar(binding, collectionData) {
  var _binding$property_val;
  const path = (_binding$property_val = binding.property_value_paths) === null || _binding$property_val === void 0 ? void 0 : _binding$property_val.color;
  if (!path) return undefined;
  const baseCssVar = collectionData.cssVariables[path];
  if (!baseCssVar) return undefined;
  const normalizedVar = normalizeCssVarName(baseCssVar);
  const {
    modifier
  } = binding;
  if (!modifier || modifier.modifier_type !== CustomWidgetModifierOperators.SHADE) {
    return `var(${normalizedVar})`;
  }
  return `var(${normalizedVar}-${modifier.modifier_value.toLowerCase()})`;
}
export function cssVarToInheritedValue(cssVar, collectionData) {
  var _entries$find;
  // Matches CSS var() syntax — e.g. `var(--hs-primary-color)` — and captures the name inside the parens
  const match = cssVar.match(/^var\(([^)]+)\)$/);
  if (!match) return undefined;
  const varName = match[1].trim();
  const entries = Object.entries(collectionData.cssVariables);
  const cssVarEntry = (_entries$find = entries.find(([, rawBase]) => varName === normalizeCssVarName(rawBase))) !== null && _entries$find !== void 0 ? _entries$find : entries.find(([, rawBase]) => varName.startsWith(`${normalizeCssVarName(rawBase)}-`));
  if (!cssVarEntry) return undefined;
  const [path, rawBase] = cssVarEntry;
  const normalizedBase = normalizeCssVarName(rawBase);
  if (varName === normalizedBase) {
    // `color` is hardcoded here as the property key because color is currently
    // the only bindable sub-property. Tracking: https://github.com/HubSpotEngineering/ReusableAssetsTeam/issues/1257
    return {
      property_value_paths: {
        color: path
      }
    };
  }
  const suffix = varName.slice(normalizedBase.length + 1);
  const shadePosition = SHADE_POSITION_NAMES.find(pos => pos.toLowerCase() === suffix);
  if (!shadePosition) return undefined;
  return {
    property_value_paths: {
      color: path
    },
    modifier: {
      modifier_type: CustomWidgetModifierOperators.SHADE,
      modifier_value: shadePosition
    }
  };
}

/**
 * Every CSS custom-property name a collection knows about, e.g.
 * `['hs-primary-color', 'hs-accent1-color']`.
 *
 * Rich text needs this list to recover a bound color that was applied via a CSS
 * class rather than an inline style: the editor compares an element's computed
 * color against the value of each of these vars to work out which one is in play.
 */
export function getCollectionCssVarNames(collectionData) {
  if (!(collectionData !== null && collectionData !== void 0 && collectionData.cssVariables)) return undefined;
  return Object.values(collectionData.cssVariables);
}

/**
 * Resolves a `var(--x)` string to a binding whose color path is a SOURCE path,
 * ready to compare against `collectionData.sourceEntries`.
 *
 * `cssVarToInheritedValue` alone can yield a CONSUMER path (e.g.
 * `theme.groupFonts.textColor.color`), which never matches a source entry - so
 * consumers are resolved through `resolvedBindings` here. Falls back to the
 * unnormalized value when the var is already a source or has no chain.
 */
export function cssVarToSourceBinding(cssVar, collectionData, resolvedBindings) {
  var _inheritedValue$prope2, _resolvedBindings$for;
  if (!cssVar) return undefined;
  const inheritedValue = cssVarToInheritedValue(cssVar, collectionData);
  if (!inheritedValue) return undefined;
  const consumerPath = (_inheritedValue$prope2 = inheritedValue.property_value_paths) === null || _inheritedValue$prope2 === void 0 ? void 0 : _inheritedValue$prope2.color;
  if (!consumerPath) return inheritedValue;
  const formRelativePath = consumerPath.startsWith(THEME_PATH_PREFIX) ? consumerPath.slice(THEME_PATH_PREFIX.length) : consumerPath;
  return (_resolvedBindings$for = resolvedBindings[formRelativePath]) !== null && _resolvedBindings$for !== void 0 ? _resolvedBindings$for : inheritedValue;
}